/*
  Advertorial behaviour (layout/theme.advertorial.liquid). No dependencies.

  1. CTA links carry the visitor's query string (utm_*, fbclid, ...) to the
     offer page, so attribution survives the click.
  2. CTA clicks are published as a Customer Events custom event.
  3. The hero video fades in over its poster once it can play.
  4. Below-the-fold videos only start downloading ~200px before they scroll
     into view.
  5. The sticky CTA bar slides in after the visitor scrolls 1200px, and sits
     on the visible bottom edge on mobile browsers with collapsing toolbars.
  6. "Last updated" dates are recomputed in the browser, because Shopify can
     serve a cached page rendered on an earlier day.
*/
;(() => {
  // Shopify's own params: `view` would switch the offer page's template
  const NOT_FORWARDED = ['view', 'preview_theme_id', 'section_id']

  const forwardQueryParams = () => {
    const incoming = new URLSearchParams(window.location.search)
    if (!incoming.toString()) {
      return
    }
    for (const link of document.querySelectorAll('a[data-adv-cta]')) {
      const url = new URL(link.href, window.location.origin)
      for (const [key, value] of incoming) {
        if (!NOT_FORWARDED.includes(key) && !url.searchParams.has(key)) {
          url.searchParams.append(key, value)
        }
      }
      link.href = url.toString()
    }
  }

  const trackCtaClicks = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-adv-cta]')
      const publish = window.Shopify?.analytics?.publish
      if (!link || typeof publish !== 'function') {
        return
      }
      publish('advertorial_cta_click', {
        page: link.closest('[data-adv-page]')?.dataset.advPage || '',
        label: link.dataset.advCta,
        destination: link.href
      })
    })
  }

  const revealHeroVideo = () => {
    for (const video of document.querySelectorAll('[data-adv-hero-video]')) {
      const show = () => video.classList.add('is-ready')
      if (video.readyState >= 3) {
        show()
      } else {
        video.addEventListener('canplay', show, { once: true })
      }
    }
  }

  const lazyLoadVideos = () => {
    const videos = document.querySelectorAll('video[data-adv-lazy-src]')
    const load = (video) => {
      const source = document.createElement('source')
      source.src = video.dataset.advLazySrc
      source.type = 'video/mp4'
      video.appendChild(source)
      video.removeAttribute('data-adv-lazy-src')
    }
    if (!('IntersectionObserver' in window)) {
      videos.forEach(load)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            load(entry.target)
            observer.unobserve(entry.target)
          }
        }
      },
      { rootMargin: '200px' }
    )
    videos.forEach((video) => observer.observe(video))
  }

  const initStickyBar = () => {
    const bar = document.querySelector('[data-adv-sticky]')
    if (!bar) {
      return
    }
    const revealAt = Number(bar.dataset.advStickyAfter) || 0
    const viewport = window.visualViewport
    let frame = 0

    const update = () => {
      frame = 0
      bar.classList.toggle('is-visible', window.scrollY >= revealAt)
      if (viewport) {
        const chrome = window.innerHeight - viewport.height - viewport.offsetTop
        bar.style.bottom = `${Math.max(0, chrome)}px`
      }
    }
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update)
      }
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    viewport?.addEventListener('resize', schedule)
    viewport?.addEventListener('scroll', schedule)
  }

  const refreshDates = () => {
    for (const el of document.querySelectorAll('[data-adv-date-offset]')) {
      const date = new Date()
      date.setDate(date.getDate() - Number(el.dataset.advDateOffset))
      el.textContent = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }

  forwardQueryParams()
  trackCtaClicks()
  revealHeroVideo()
  lazyLoadVideos()
  initStickyBar()
  refreshDates()
})()
